import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';

import {
  DM_ONBOARDING_CARD_IDS,
  ONBOARDING_CARD_IDS,
  claimOnboardingDeck,
  hasOnboardingDeckClaim,
  remainingOnboardingCards,
} from '../../lib/onboardingCardDismissal';
import { thumbhashToAverageRGBA } from '../../lib/thumbdata';

import { useRouteWorkspace } from '../../contexts/RouteWorkspaceContext';
import { END_OF_MESSAGES_EMPTY_HEIGHT_PX, endOfMessagesHeight } from '../../feed/echo/endOfMessagesGeometry';
import useDirectMessageBackground from '../../hooks/useDirectMessageBackground';
import OnboardingCardStack from '../shared/OnboardingCardStack';

const ChannelCard = memo(function ChannelCard({ channel }) {
  const backgroundColor = useMemo(() => {
    const flexicon = channel?.flexicon;
    const thumbhash = flexicon?.kind === 'image' ? flexicon?.image?.metadata?.thumbhash : flexicon?.[flexicon?.kind]?.thumbhash;
    if (!thumbhash) return undefined;

    const rgba = thumbhashToAverageRGBA(thumbhash);
    return `rgb(${Math.round(rgba.r * 255)}, ${Math.round(rgba.g * 255)}, ${Math.round(rgba.b * 255)})`;
  }, [channel]);
  const backgroundStyle = useMemo(() => (backgroundColor ? { backgroundColor } : undefined), [backgroundColor]);

  return (
    <div className="relative flex h-80 w-full shrink-0 flex-col items-center justify-center overflow-hidden rounded-4xl border border-white px-4 dark:border-white/5">
      <div
        className="pointer-events-none absolute inset-0 bg-gray-400 opacity-20 saturate-80 dark:bg-neutral-800"
        style={backgroundStyle}
      />
      <div
        className="pointer-events-none absolute -top-80 left-1/2 h-100 w-200 -translate-x-1/2 rounded-full bg-gray-400 blur-[120px] saturate-80 dark:bg-neutral-800"
        style={backgroundStyle}
      />
      <div className="pointer-events-none absolute top-60 left-1/2 h-100 w-160 -translate-x-1/2 rounded-full bg-white opacity-50 blur-[120px] dark:bg-neutral-900" />
    </div>
  );
});

function ChannelWelcome({ displayName }) {
  return (
    <div className="flex h-13 w-full shrink-0 items-center justify-center px-4 text-center text-2xl/8 font-semibold text-black dark:text-neutral-200">
      <span className="max-w-4/5 truncate">{displayName}</span>
    </div>
  );
}

const EndOfMessages = memo(({ eom, currentChannel = null, currentChannelBreadcrumbs = [], isSharedChannel = false }) => {
  const { isDirectMessage = false, directMessage, mainChannelId, profile, profileId, workspace } = useRouteWorkspace();
  // A DM's background lives on the DM record and saves through the DM endpoint — its
  // hidden workspace can't be PATCHed by participants, so the deck's background card
  // gets the DM-shaped resource and save path instead of the workspace defaults.
  const dmBackground = useDirectMessageBackground();
  const workspaceId = workspace?.id ? String(workspace.id) : null;
  const onboardingScope = `${workspaceId || ''}:${profileId || ''}`;
  const isMainChannel = Boolean(currentChannel?.id && mainChannelId && String(currentChannel.id) === String(mainChannelId));
  const isMainOrSharedChannel = isMainChannel || isSharedChannel;
  // The deck belongs to the creator alone, held as a per-device claim. Groups claim at
  // creation (NewWorkspacePanel) — the role check can't isolate the creator because every
  // joiner is an admin under the temporary all-admin default. DMs claim by being seen
  // empty: the peer can't open a DM until the first real message reveals it
  // (show_for_both!), so only the creator ever qualifies, and the claim keeps their deck
  // through later messages.
  const holdsDeckClaim = isDirectMessage
    ? !directMessage?.last_message_sort_order || hasOnboardingDeckClaim(workspaceId, profileId)
    : hasOnboardingDeckClaim(workspaceId, profileId);
  const isOnboardingAdmin = !isSharedChannel && profile?.role === 'admin' && holdsDeckClaim;

  useEffect(() => {
    if (!isDirectMessage || !workspaceId || !profileId) return;
    if (directMessage?.last_message_sort_order) return;
    claimOnboardingDeck(workspaceId, profileId);
  }, [directMessage?.last_message_sort_order, isDirectMessage, profileId, workspaceId]);
  const onboardingCardIds = isDirectMessage ? DM_ONBOARDING_CARD_IDS : ONBOARDING_CARD_IDS;
  // Breakouts render no header at all — the workspace sections belong to the main
  // channel, and a breakout needs no card of its own (see isEmpty below).
  const isOpenBreakout = currentChannel?.kind === 'open_breakout';
  const scopedOnboardingDismissal = useMemo(
    () => ({
      dismissed: remainingOnboardingCards(workspaceId, profileId, onboardingCardIds).length === 0,
      scope: onboardingScope,
    }),
    [onboardingCardIds, onboardingScope, profileId, workspaceId],
  );
  const [onboardingDismissal, setOnboardingDismissal] = useState(scopedOnboardingDismissal);
  const onboardingDismissed =
    onboardingDismissal.scope === onboardingScope ? onboardingDismissal.dismissed : scopedOnboardingDismissal.dismissed;

  useEffect(() => {
    setOnboardingDismissal(scopedOnboardingDismissal);
  }, [scopedOnboardingDismissal]);

  // Only fires once the last card in the deck is gone — dismissing one of three just
  // slides the next forward, and the terminal header keeps its height.
  const handleOnboardingEmptied = useCallback(() => {
    setOnboardingDismissal({ dismissed: true, scope: onboardingScope });
  }, [onboardingScope]);

  const showOnboardingCards = isOnboardingAdmin && !onboardingDismissed;
  // Main channels only render the onboarding deck now (the intro avatar and invite
  // button are gone), so without it there is no terminal header at all.
  const isEmpty = !currentChannel || isOpenBreakout || (isMainOrSharedChannel && !showOnboardingCards);
  const height = endOfMessagesHeight({
    hasCurrentChannel: Boolean(currentChannel),
    isMainOrSharedChannel,
    isOpenBreakout,
    showOnboardingCards,
  });
  const displayName = currentChannel?.name || currentChannelBreadcrumbs[currentChannelBreadcrumbs.length - 1]?.name || '';

  if (isEmpty) {
    return (
      <div
        className="w-full"
        style={{ height: END_OF_MESSAGES_EMPTY_HEIGHT_PX }}
        data-debug-id={eom.id}
        data-debug-sort-order={eom.sort_order}
        data-end-of-messages
        data-end-of-messages-height={height}
      />
    );
  }

  // Keep the shared geometry as the pre-mount estimate, but let the rendered
  // sections determine DOM height. ItemShell observes this intrinsic size, so a
  // section that appears, disappears, or grows cannot paint outside a stale
  // fixed-height row and make the terminal content unreachable.
  return (
    <div
      className="flex w-full max-w-full shrink-0 flex-col items-center gap-4 pt-24"
      data-debug-id={eom.id}
      data-debug-sort-order={eom.sort_order}
      data-end-of-messages
      data-end-of-messages-height={height}
    >
      {showOnboardingCards ? (
        // The negative inset cancels the message row's pl-[22px], which exists to line bubble
        // text up with the composer. Inheriting it pushes the deck off-centre and costs 22px
        // of width on narrow screens, where the card should reach the panel's own px-4 gutter.
        <div className="-ml-[22px] flex w-[calc(100%+22px)] justify-center">
          <OnboardingCardStack
            key={onboardingScope}
            cardIds={onboardingCardIds}
            workspace={isDirectMessage ? dmBackground.backgroundResource : workspace}
            profileId={profileId}
            backgroundSaveAttachment={isDirectMessage ? dmBackground.saveAttachment : undefined}
            backgroundSavePreset={isDirectMessage ? dmBackground.savePreset : undefined}
            // The header floats over the feed, so the pane's top is y=0: the title pill sits
            // at pt-4 and is h-9, putting its bottom edge at 52px. The row's own pt-24 (96)
            // plus mt-9 (36) puts the front card at 132, and the peeks — transformed 32px up
            // and outside layout — top out at 100. That's the 48px below the pill.
            //
            // mb-25 still does the lifting: the feed is anchored to the bottom, so trimming
            // space above the deck only drags the messages below up with it.
            className="mt-9 mb-25 pb-6"
            onEmpty={handleOnboardingEmptied}
          />
        </div>
      ) : null}
      {!isMainOrSharedChannel ? (
        <>
          <ChannelCard channel={currentChannel} />
          <ChannelWelcome displayName={displayName} />
        </>
      ) : null}
    </div>
  );
});

EndOfMessages.displayName = 'EndOfMessages';

export default EndOfMessages;
